/**
 * rehype plugin: surface the latent structure in the coach's prose.
 *
 * The model writes plain paragraphs (no markdown bold) but leans on short
 * "Label:" lead-ins to open sections — e.g. "Quick check on your reasoning:",
 * "The case for betting:", "Bottom line:". Rendered raw, those vanish into a
 * grey wall of text. This plugin detects a short capitalized lead-in at the
 * very start of a paragraph / list item and wraps it (with its colon) in
 * `<strong class="lead-in">`, which the prose-chat CSS styles as an inline
 * section header.
 *
 * The match is deliberately tight to avoid mid-sentence colons and ratios:
 *   - starts the block (first text node), begins with a capital letter
 *   - 1–46 chars before the colon, none of them sentence punctuation . ! ? :
 *   - colon must be followed by whitespace
 * So "Bottom line: fold" matches; "the ratio is 2:1" / "...value bet thin: it"
 * (long, or digit-led) do not.
 */

// Capital-led short phrase, then `:`, then a space. Group 1 keeps the colon.
const LEAD_IN_RE = /^([A-Z][^.!?:\n]{1,46}:)(\s)/;

interface TextNode {
  type: "text";
  value: string;
}
interface ElementNode {
  type: "element";
  tagName: string;
  properties?: Record<string, unknown>;
  children: HastNode[];
}
type HastNode =
  | TextNode
  | ElementNode
  | { type: string; value?: string; children?: HastNode[] };

function hasChildren(
  node: HastNode,
): node is { type: string; children: HastNode[] } {
  return Array.isArray((node as { children?: unknown }).children);
}

/** Wrap a leading "Label:" in a <strong class="lead-in"> on one block. */
function applyLeadIn(el: ElementNode): void {
  const first = el.children[0];
  if (!first || first.type !== "text") return;
  const match = LEAD_IN_RE.exec((first as TextNode).value);
  if (!match) return;
  const label = match[1]!; // includes the trailing colon
  const rest = (first as TextNode).value.slice(label.length); // keeps the space
  el.children = [
    {
      type: "element",
      tagName: "strong",
      properties: { className: ["lead-in"] },
      children: [{ type: "text", value: label }],
    },
    { type: "text", value: rest },
    ...el.children.slice(1),
  ];
}

function walk(node: HastNode): void {
  if (!hasChildren(node)) return;
  for (const child of node.children) {
    if (
      child.type === "element" &&
      ((child as ElementNode).tagName === "p" ||
        (child as ElementNode).tagName === "li")
    ) {
      applyLeadIn(child as ElementNode);
    }
    walk(child);
  }
}

export function rehypeLeadIns() {
  return (tree: HastNode): void => walk(tree);
}

// Exported for unit testing.
export const __test = { applyLeadIn, LEAD_IN_RE };
